import { useTranslation } from 'react-i18next';
import type { NewReport } from '../../hooks/useNewReport';
import { EditorModal } from './EditorModal';
import { PresetBar } from './editorStyles';
import { MetricEditor } from './MetricEditor';
import { Action, ErrorText, Hint } from './styles';
import { hasWidth, type EditorModalState, type OverviewEditor } from './useOverviewEditor';

type MetricState = Extract<NonNullable<EditorModalState>, { kind: 'metric' }>;

/** Edits a metric live in the draft so the preview recalculates; Cancel restores the document as it was on open. */
export function MetricModal({
  report,
  editor,
  state,
  onClose,
}: {
  report: NewReport;
  editor: OverviewEditor;
  state: MetricState;
  onClose: () => void;
}) {
  const { t } = useTranslation('newReport');
  const { controller, document, components } = editor;
  const metric = document.metrics.find((item) => item.id === state.metricId);
  const placement = components.find((component) => component.id === state.placementId);
  const error = controller.metricErrors[state.metricId];
  const placing = state.create && state.index !== undefined;
  const cancel = () => {
    controller.restore(state.snapshot);
    onClose();
  };
  const apply = () => {
    if (placing) editor.placeMetric(state.metricId, state.index);
    onClose();
  };
  const highlightSources = editor.reportableQuestions
    .filter((question) => question.answersToHighlight?.length)
    .map((question) => ({
      questionIndex: question.index,
      optionIndexes: [...new Set((question.answersToHighlight ?? []).map(Number))],
    }));
  if (!metric) return null;
  return (
    <EditorModal
      size="large"
      title={t(state.create ? 'definition.createMetric' : 'definition.metricSettings')}
      onClose={cancel}
      footer={
        <>
          {error ? (
            <ErrorText style={{ flex: 1, margin: 0 }}>
              {t('definition.invalid', { reason: t(`definition.validation.${error}`) })}
            </ErrorText>
          ) : (
            <Hint style={{ flex: 1 }}>
              {t(placing ? 'definition.metricWillBePlaced' : 'definition.metricPreviewHelp')}
            </Hint>
          )}
          <Action onClick={cancel}>{t('definition.cancel')}</Action>
          <Action primary disabled={!!error} onClick={apply}>
            {t(placing ? 'definition.addMetricToReport' : state.create ? 'definition.createMetric' : 'definition.done')}
          </Action>
        </>
      }
    >
      {state.create && highlightSources.length > 0 && (
        <PresetBar>
          <Hint style={{ flex: 1 }}>{t('definition.presetHelp')}</Hint>
          <Action
            onClick={() =>
              editor.upsertMetric({
                ...metric,
                title: { [editor.language]: t('definition.withIssues') },
                calculation: { type: 'choiceMatchRate', sources: highlightSources, complement: false },
                presentation: { renderer: 'bar', format: 'maxPercent' },
              })
            }
          >
            {t('definition.withIssuesPreset')}
          </Action>
        </PresetBar>
      )}
      <MetricEditor
        metric={metric}
        metrics={document.metrics}
        questions={editor.reportableQuestions}
        language={editor.language}
        onChange={editor.upsertMetric}
        error={error}
        summary={report.currentGroup?.customMetrics[metric.id]}
        width={placement && hasWidth(placement) ? placement.width ?? 'full' : undefined}
        onWidthChange={placement ? (width) => editor.updateComponent({ ...placement, width }) : undefined}
      />
    </EditorModal>
  );
}
